import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { motion } from "framer-motion";
import {
  Lightning, Microphone, PenNib, Strategy, Archive, ArrowsClockwise, UsersFour,
  Sun, Moon, ArrowRight, Check, Crown, Buildings, Rocket,
  Briefcase, Chalkboard, UserCircle, PencilSimple, UsersThree, User
} from "@phosphor-icons/react";

const FEATURES = [
  { icon: Microphone, title: "Brand Voice System", desc: "Capture your tone, positioning, and frameworks once. Every piece of content sounds like you — not a chatbot." },
  { icon: PenNib, title: "Purpose-Built Builders", desc: "Newsletters, lead magnets, email sequences, bulk variations — each with its own structured workflow." },
  { icon: Strategy, title: "Strategy-to-Language Engine", desc: "Turn positioning, offers, and audience insight into publishable drafts that stay aligned to the brand." },
  { icon: Archive, title: "Resource Bank", desc: "Upload reference content, frameworks, and top performers. The AI learns from what already works for you." },
  { icon: ArrowsClockwise, title: "Repurpose & Scale", desc: "Turn one piece into many. Repurpose across platforms and formats in a single pass — or chain them." },
  { icon: UsersFour, title: "Multi-Client Control Center", desc: "Keep every client voice separated, review-ready, and consistent across campaigns from one workspace." },
];

const PERSONAS = [
  { icon: Briefcase, title: "Strategic Advisors", desc: "Manage multiple brand voices without losing quality. Scale content across clients from a single workspace." },
  { icon: Chalkboard, title: "Consultants", desc: "Turn your expertise into authority content. Show up consistently without the production overhead." },
  { icon: UserCircle, title: "Coaches", desc: "Speak to your audience in your voice. Build trust with content that actually sounds like your coaching." },
  { icon: PencilSimple, title: "Freelancers", desc: "Produce client content faster without sacrificing quality. More output, fewer revisions." },
  { icon: UsersThree, title: "Agencies", desc: "Manage client workspaces, approval workflows, and team access — all under one roof." },
  { icon: User, title: "Solopreneurs", desc: "Show up everywhere without hiring a team. Your voice, your strategy, your content — automated." },
];

const STEPS = [
  { num: "01", title: "Build the brief", desc: "Define audience, objective, angle, and key message before drafting. Strategy comes first." },
  { num: "02", title: "Lock voice context", desc: "Apply the right brand voice, positioning, and source material for this exact client or campaign." },
  { num: "03", title: "Draft, score, publish", desc: "Generate, refine, and approve writing that sounds right before anything goes live." },
];

const FREE_FEATURES = [
  "25 AI generations per month",
  "All core builders included",
  "Brand voice personalization",
  "5 resource bank files",
  "AI key included (system-managed)",
];

const PLAN_HIGHLIGHTS = [
  { label: "Creator", price: "$49", icon: Rocket, desc: "500 gen/mo, 10 workspaces, brand voice wizard" },
  { label: "Studio", price: "$149", icon: Crown, desc: "Unlimited gen, 50 workspaces, client approval portal" },
  { label: "Agency", price: "$299", icon: Buildings, desc: "Unlimited gen, BYOK, 5 seats, team controls" },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  if (user) {
    navigate("/dashboard", { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen bg-[var(--bg-base)]" data-testid="landing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-[var(--bg-base)]/80 backdrop-blur-xl border-b border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <img src="/web_logo.svg" alt="Quibn" className="h-9 w-auto" data-testid="landing-logo" />
          <span className="text-[10px] text-[var(--text-muted)] ml-1.5 hidden sm:inline">Pronounced "KWIB-in"</span>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/pricing")} className="text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors hidden sm:block" data-testid="landing-pricing-link">
              Pricing
            </button>
            <button onClick={toggleTheme} className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-raised)] transition-colors" data-testid="landing-theme-toggle">
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button onClick={() => navigate("/login")} className="text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors" data-testid="landing-sign-in">
              Sign In
            </button>
            <button onClick={() => navigate("/login")} className="px-4 py-2 rounded-lg bg-[#FF4400] hover:bg-[#E63E00] text-white text-sm font-semibold transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#FF4400]/20" data-testid="landing-get-started">
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 pb-24 sm:pt-28 sm:pb-32">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--accent-subtle)] text-[#FF4400] text-xs font-semibold mb-6 border border-[#FF4400]/20">
              <Lightning size={14} weight="fill" />
              Built for consultants, coaches, freelancers, agencies, and strategic advisors
            </div>
            <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[var(--text-primary)] tracking-tight leading-[1.1] mb-6">
              Where writing<br />
              <span className="text-[#FF4400]">begins.</span>
            </h1>
            <p className="text-lg text-[var(--text-secondary)] leading-relaxed max-w-xl mb-4">
              For the people responsible for content, marketing, and brand voice, Quibn turns strategy into clear, publishable language that sounds like the brand behind it.
            </p>
            <p className="text-sm text-[var(--text-muted)] leading-relaxed max-w-xl mb-8">
              Quibn is the only writing system built for the people responsible for making the brand sound right — especially multi-client experts who need consistency at scale.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => navigate("/login")} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#FF4400] hover:bg-[#E63E00] text-white text-sm font-semibold transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#FF4400]/25" data-testid="hero-cta">
                Start Writing Free <ArrowRight size={16} weight="bold" />
              </button>
              <button onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-[var(--border-strong)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-raised)] text-sm font-medium transition-all">
                See How It Works
              </button>
            </div>
          </motion.div>
        </div>
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-[#FF4400]/[0.04] rounded-full blur-[120px] pointer-events-none" />
      </section>

      {/* Manifesto */}
      <section className="py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <motion.blockquote
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="font-['Cormorant_Garamond'] text-xl sm:text-2xl lg:text-3xl font-medium text-[var(--text-primary)] leading-relaxed italic tracking-tight"
          >
            "Writing is not just output. It is positioning in public. It is authority made visible. It is marketing in language form."
          </motion.blockquote>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-20 sm:py-28 bg-[var(--bg-card)] border-y border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mb-14">
            <p className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[#FF4400] mb-3">Who it's for</p>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[var(--text-primary)] tracking-tight mb-4">
              Built for the people behind the brand
            </h2>
            <p className="text-[var(--text-secondary)] leading-relaxed">
              If you own message quality across one brand or twenty, Quibn gives you one place to make the writing right.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PERSONAS.map((p, i) => (
              <motion.div key={p.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="group p-6 rounded-xl border border-[var(--border)] bg-[var(--bg-base)] hover:border-[#FF4400]/30 hover:shadow-sm transition-all">
                <div className="w-10 h-10 rounded-lg bg-[var(--accent-subtle)] flex items-center justify-center mb-4 group-hover:bg-[#FF4400]/15 transition-colors">
                  <p.icon size={20} weight="duotone" className="text-[#FF4400]" />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-base font-semibold text-[var(--text-primary)] mb-2">{p.title}</h3>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mb-14">
            <p className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[#FF4400] mb-3">What you get</p>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[var(--text-primary)] tracking-tight mb-4">
              A system, not a shortcut
            </h2>
            <p className="text-[var(--text-secondary)] leading-relaxed">
              A complete strategy-to-language workflow designed for teams and experts who cannot afford generic, off-brand output.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="group p-6 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] hover:border-[#FF4400]/30 hover:shadow-sm transition-all">
                <div className="w-10 h-10 rounded-lg bg-[var(--accent-subtle)] flex items-center justify-center mb-4 group-hover:bg-[#FF4400]/15 transition-colors">
                  <f.icon size={20} weight="duotone" className="text-[#FF4400]" />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-base font-semibold text-[var(--text-primary)] mb-2">{f.title}</h3>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 sm:py-28 bg-[var(--bg-card)] border-y border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mb-14">
            <p className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[#FF4400] mb-3">How it works</p>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[var(--text-primary)] tracking-tight">
              From strategy to publish without drift
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((s, i) => (
              <motion.div key={s.num} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <span className="font-['JetBrains_Mono'] text-4xl font-bold text-[#FF4400]/20">{s.num}</span>
                <h3 className="font-['Cormorant_Garamond'] text-lg font-semibold text-[var(--text-primary)] mt-2 mb-2">{s.title}</h3>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--accent-subtle)] text-[#FF4400] text-xs font-semibold mb-4 border border-[#FF4400]/20">
              Currently in Beta
            </div>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[var(--text-primary)] tracking-tight mb-4">
              Plans that grow with you
            </h2>
            <p className="text-[var(--text-secondary)] max-w-lg mx-auto">
              Start free during beta. Upgrade when you're ready for more power.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-8">
            {/* Free Beta */}
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="relative p-8 rounded-2xl border-2 border-[#FF4400]/40 bg-[var(--bg-card)] shadow-sm" data-testid="pricing-free">
              <div className="absolute -top-3 left-6">
                <span className="px-3 py-1 rounded-full bg-[#FF4400] text-white text-xs font-semibold">Free Beta</span>
              </div>
              <div className="mt-2 mb-6">
                <span className="font-['Cormorant_Garamond'] text-4xl font-bold text-[var(--text-primary)]">$0</span>
                <span className="text-[var(--text-muted)] text-sm ml-2">/ month</span>
              </div>
              <ul className="space-y-3 mb-8">
                {FREE_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-[var(--text-secondary)]">
                    <Check size={16} weight="bold" className="text-[#FF4400] mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate("/login")} className="w-full py-3 rounded-lg bg-[#FF4400] hover:bg-[#E63E00] text-white font-semibold text-sm transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#FF4400]/25" data-testid="pricing-free-cta">
                Get Started Free
              </button>
            </motion.div>

            {/* Paid Plans Summary */}
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
              className="relative p-8 rounded-2xl border border-[var(--border)] bg-[var(--bg-card)]" data-testid="pricing-paid-preview">
              <div className="absolute -top-3 left-6">
                <span className="px-3 py-1 rounded-full bg-[var(--bg-raised)] text-[var(--text-secondary)] text-xs font-semibold border border-[var(--border-strong)]">Paid Plans</span>
              </div>
              <div className="mb-6 mt-1">
                <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[var(--text-primary)] mb-2">Scale your output</h3>
                <p className="text-sm text-[var(--text-muted)]">More power, more workspaces, team seats.</p>
              </div>
              <div className="space-y-4 mb-8">
                {PLAN_HIGHLIGHTS.map((p) => (
                  <div key={p.label} className="flex items-center gap-3 p-3 rounded-lg bg-[var(--bg-base)] border border-[var(--border)]">
                    <p.icon size={20} weight="fill" className="text-[var(--text-secondary)] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-[var(--text-primary)]">{p.label}</span>
                        <span className="text-sm font-bold text-[var(--text-primary)]">{p.price}<span className="text-xs text-[var(--text-muted)] font-normal">/mo</span></span>
                      </div>
                      <p className="text-xs text-[var(--text-muted)] mt-0.5">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => navigate("/pricing")} className="w-full py-3 rounded-lg border border-[var(--border-strong)] text-[var(--text-secondary)] font-semibold text-sm hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)] transition-all" data-testid="pricing-view-all">
                View All Plans <ArrowRight size={14} weight="bold" className="inline ml-1" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 sm:py-28 bg-[var(--bg-card)] border-y border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[var(--text-primary)] tracking-tight mb-4">
            Quibn. Where writing begins.
          </h2>
          <p className="text-[var(--text-secondary)] max-w-lg mx-auto mb-8">
            Pronounced "KWIB-in." Build the brief, align the voice, and publish with confidence.
          </p>
          <button onClick={() => navigate("/login")} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg bg-[#FF4400] hover:bg-[#E63E00] text-white font-semibold transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#FF4400]/25" data-testid="cta-final">
            Get Started Free <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center">
            <img src="/web_logo.svg" alt="Quibn" className="h-7 w-auto" />
            <span className="text-xs text-[var(--text-muted)] ml-3">Where writing begins.</span>
          </div>
          <div className="flex items-center gap-4 flex-wrap justify-center sm:justify-end">
            <a href="/legal/terms" className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)]">Terms</a>
            <a href="/legal/privacy" className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)]">Privacy</a>
            <a href="/legal/billing" className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)]">Billing & Refunds</a>
            <a href="/legal/credits" className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)]">Credit Policy</a>
            <a href="/legal/data-retention" className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)]">Data Retention</a>
            <a href="/fair-use-policy" className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)]">Fair Use</a>
            <span className="text-xs text-[var(--text-muted)]">&copy; {new Date().getFullYear()}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
